<script setup lang="ts">
  import type { PropType } from 'vue'

  import { HLAlert } from '@platform-ui/highrise'
  import type { HLAlertType } from '@platform-ui/highrise'

  const props = defineProps({
    id: {
      type: String,
      required: true,
    },
    color: {
      type: String as PropType<'blue' | 'gray' | 'red' | 'orange' | 'green' | 'white'>,
      default: undefined,
    },
    closable: {
      type: Boolean,
      default: undefined,
    },
    title: {
      type: String,
      default: undefined,
    },
    content: {
      type: String,
      default: undefined,
    },
    role: {
      type: String,
      default: undefined,
    },
    autoClose: {
      type: [Boolean, Number] as PropType<boolean | number>,
      default: undefined,
    },
    actionOne: {
      type: Object as PropType<{
        text?: string
        iconLeft?: string
        iconRight?: string
        link?: string
        loading?: boolean
        disabled?: boolean
        ariaLabel?: string
        onActionClick?: () => void
      }>,
      default: undefined,
    },
    actionTwo: {
      type: Object as PropType<{
        text?: string
        iconLeft?: string
        iconRight?: string
        link?: string
        loading?: boolean
        disabled?: boolean
        ariaLabel?: string
        onActionClick?: () => void
      }>,
      default: undefined,
    },
    width: {
      type: String,
      default: undefined,
    },
    height: {
      type: String,
      default: undefined,
    },
    maxHeight: {
      type: String,
      default: undefined,
    },
    maxWidth: {
      type: String,
      default: undefined,
    },
    type: {
      type: String as PropType<HLAlertType>,
      default: undefined,
    },
  })

  defineEmits<{
    close: [id: string]
    actionOne: [event: Event]
    actionTwo: [event: Event]
  }>()

  defineOptions({ name: 'CPAlert', inheritAttrs: false })
</script>

<template>
  <HLAlert
    v-bind="$attrs"
    :id="props.id"
    :color="props.color"
    :closable="props.closable"
    :title="props.title"
    :content="props.content"
    :role="props.role"
    :auto-close="props.autoClose"
    :action-one="props.actionOne"
    :action-two="props.actionTwo"
    :width="props.width"
    :height="props.height"
    :max-height="props.maxHeight"
    :max-width="props.maxWidth"
    :type="props.type"
    @close="$emit('close', $event)"
    @action-one="$emit('actionOne', $event)"
    @action-two="$emit('actionTwo', $event)"
  >
    <template v-if="$slots.icon" #icon>
      <slot name="icon" />
    </template>
    <template v-if="$slots.alertActions" #alertActions>
      <slot name="alertActions" />
    </template>
    <slot />
  </HLAlert>
</template>
